<script setup lang="ts">
import EditIcon from "@/components/icon/editIcon.vue";
import MagicStickIcon from "@/components/icon/MagicStickIcon.vue";
import CopyIcon from "@/components/icon/copyIcon.vue";
import { Popup, showLoadingToast, showSuccessToast } from "vant";
import { useRouter } from "vue-router";
import { ref, watch } from "vue";
import { useStory } from "@/stores/story";
import StoryInfoEdit from "@/components/mobile/story/story-chapter/StoryInfoEdit.vue";
import FontPlusIcon from "@/components/icon/FontPlusIcon.vue";
import StorySelectionPopup from "@/components/mobile/popup/StorySelectionPopup.vue";
import { Story, StoryBook } from "@/types/entity";
import { useStoryBook } from "@/stores/story-book";
import i18n from "@/locales";
// Props
const props = defineProps<{
  show: boolean;
  storyBook: StoryBook;
  enableDuplicate: boolean;
}>();
const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
}>();
// State
const storyStore = useStory();
const storyBookStore = useStoryBook();
const router = useRouter();
const showCreateInfo = ref(false);
const showDuplicateSelection = ref(false);
const toDuplicateStory = ref<Story | null>(null);

// Lifecycle
watch(
  () => props.show,
  (value) => {
    if (value) {
      showCreateInfo.value = false;
    }
  }
);
watch(showDuplicateSelection, (value) => {
  if (value) {
    toDuplicateStory.value = null;
  }
});
// Event Handler
const onTriggerDuplicate = () => {
  emit("update:show", false);
  showDuplicateSelection.value = true;
};
const onConfirmDuplicate = async (story: Story) => {
  const loadingToast = showLoadingToast({
    message: i18n.global.t("Message.chapterCopying"),
    duration: 0,
  });
  try {
    await storyStore.duplicateStory(story.id);
    showDuplicateSelection.value = false;
    loadingToast.close();
    showSuccessToast(i18n.global.t("Message.chapterCopiedSuccessfully"));
  } finally {
    loadingToast.close();
  }
};

const clickAutoGenerate = () => {
  const { id } = props.storyBook;
  storyBookStore.initForm(id);
  router.push(`/story/edit?id=${id}`);
};
</script>
<template>
  <popup
    :show="show"
    @update:show="emit('update:show', $event)"
    position="center"
    class="w-[350px] py-10"
    round
    teleport="body"
  >
    <div
      v-if="!showCreateInfo"
      class="flex flex-col items-center justify-center w-full h-full gap-10"
    >
      <div class="flex flex-col items-center justify-center gap-2">
        <font-plus-icon class="w-[20%] text-base-content/50" />
        <div class="text-lg font-bold text-base-content/50">
          {{ $t("story.newChapter") }}
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <!-- <button
          class="w-60 btn rounded btn-sm btn-primary"
          @click="clickAutoGenerate"
        >
          <magic-stick-icon class="icon-base" />
          {{ $t("Buttons.autoGenerate") }}
        </button> -->
        <button
          class="w-60 btn rounded btn-sm btn-primary"
          @click="showCreateInfo = true"
        >
          <edit-icon />
          {{ $t("Buttons.manualCreation") }}
        </button>
        <button
          class="w-60 btn rounded btn-sm btn-primary"
          v-if="enableDuplicate"
          @click="onTriggerDuplicate"
        >
          <copy-icon class="icon-sm" />
          {{ $t("Buttons.copy") }}
        </button>
        <button
          class="btn w-60 rounded btn-sm"
          @click="emit('update:show', false)"
        >
          {{ $t("Buttons.close") }}
        </button>
      </div>
    </div>
    <story-info-edit
      v-else
      :story-book-id="storyBook.id"
      @on-success="emit('update:show', false)"
      @on-cancel="emit('update:show', false)"
    />
  </popup>
  <story-selection-popup
    v-if="enableDuplicate"
    v-model:show="showDuplicateSelection"
    v-model:selected-story="toDuplicateStory"
    :story-book="storyBook"
    @onConfirm="onConfirmDuplicate"
  />
</template>
